import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

// Helper to get auth header for edge functions
export async function getAuthHeader(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return `Bearer ${session.access_token}`;
}

// Helper to call edge functions with optional abort signal and retry logic
// Note: Supabase edge functions have a 150s timeout by default (400s max on Pro plans)
export interface CallEdgeFunctionOptions {
  signal?: AbortSignal;
  maxRetries?: number;
  initialDelayMs?: number;
  maxDelayMs?: number;
  retryOnTimeout?: boolean;
}

export async function callEdgeFunction<T>(
  functionName: string,
  body: Record<string, unknown> | object,
  options?: CallEdgeFunctionOptions
): Promise<T> {
  const {
    signal,
    maxRetries = 0,
    initialDelayMs = 1000,
    maxDelayMs = 30000,
    retryOnTimeout = false,
  } = options || {};

  const authHeader = await getAuthHeader();

  let lastError: Error | null = null;
  let attempt = 0;

  while (attempt <= maxRetries) {
    if (attempt > 0) {
      // Exponential backoff with jitter
      const delay = Math.min(
        initialDelayMs * Math.pow(2, attempt - 1) + Math.random() * 1000,
        maxDelayMs
      );
      console.log(`[EdgeFunction] Retry ${attempt}/${maxRetries} for ${functionName} after ${Math.round(delay)}ms`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    attempt++;

    let response: Response;
    try {
      response = await fetch(
        `${supabaseUrl}/functions/v1/${functionName}`,
        {
          method: 'POST',
          headers: {
            'Authorization': authHeader,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
          signal,
        }
      );
    } catch (fetchError) {
      // Network error or CORS error (often happens with 504 timeout)
      if (fetchError instanceof Error) {
        if (fetchError.name === 'AbortError') {
          throw fetchError; // Re-throw abort errors as-is - don't retry
        }
        // CORS errors or network failures often mean the edge function timed out
        const timeoutError = new Error(`Edge function request failed (possibly timed out): ${fetchError.message}`);
        (timeoutError as { isLikelyTimeout?: boolean; isRetryable?: boolean }).isLikelyTimeout = true;
        (timeoutError as { isLikelyTimeout?: boolean; isRetryable?: boolean }).isRetryable = retryOnTimeout;
        lastError = timeoutError;

        if (retryOnTimeout && attempt <= maxRetries) {
          continue; // Retry on timeout if configured
        }
        throw timeoutError;
      }
      throw new Error('Edge function request failed');
    }

    // Handle timeout specifically (504)
    if (response.status === 504) {
      const timeoutError = new Error('Edge function timed out. The operation may have completed on the server.');
      (timeoutError as { isLikelyTimeout?: boolean; isRetryable?: boolean }).isLikelyTimeout = true;
      (timeoutError as { isLikelyTimeout?: boolean; isRetryable?: boolean }).isRetryable = retryOnTimeout;
      lastError = timeoutError;

      if (retryOnTimeout && attempt <= maxRetries) {
        continue; // Retry on timeout if configured
      }
      throw timeoutError;
    }

    // Handle 5xx errors (server errors) - these are retryable
    if (response.status >= 500 && response.status !== 504) {
      const serverError = new Error(`Edge function server error: ${response.status}`);
      (serverError as { isRetryable?: boolean }).isRetryable = true;
      lastError = serverError;

      if (attempt <= maxRetries) {
        continue; // Retry server errors
      }
      throw serverError;
    }

    // Handle 429 (rate limit) - retryable with longer delay
    if (response.status === 429) {
      const rateLimitError = new Error('Edge function rate limited');
      (rateLimitError as { isRetryable?: boolean; isRateLimited?: boolean }).isRetryable = true;
      (rateLimitError as { isRetryable?: boolean; isRateLimited?: boolean }).isRateLimited = true;
      lastError = rateLimitError;

      if (attempt <= maxRetries) {
        // Longer delay for rate limits
        await new Promise((resolve) => setTimeout(resolve, 5000));
        continue;
      }
      throw rateLimitError;
    }

    // Try to parse JSON response
    let data;
    try {
      data = await response.json();
    } catch {
      // If JSON parsing fails, create a generic error based on status
      if (!response.ok) {
        throw new Error(`Edge function failed with status ${response.status}`);
      }
      throw new Error('Edge function returned invalid response');
    }

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Edge function call failed');
    }

    return data as T;
  }

  // If we exit the loop, throw the last error
  throw lastError || new Error('Edge function failed after retries');
}
